import { useCallback } from "react";

import { useAppDispatch, useAppSelector } from "../Store/Hooks";
import { generateKpiBriefing } from "../Store/Reducers/XFactorKpiSlice";

export type XFactorKpiGenerateState = {
  /** Trues while THIS account's generation call is in flight — keyed so a
   * matrix of per-row buttons can show a spinner on just the one row that
   * was clicked, not the whole table. */
  generatingAccountId: string | null;
  error: string | null;
};

/**
 * POST /kpi/generate — the only way native KPI data ever gets created for
 * an account/date (no cron, no auto-generate-on-view; see
 * [[kpi-briefing-belongs-with-execbridge]]). Ops-gated server-side
 * (`_require_ops` in routers/xfactor_briefing.py) — a non-ops caller gets a
 * 403 back through `error`, surfaced as-is rather than hidden client-side.
 *
 * There is no provider choice: KPI generation always runs on Bedrock
 * server-side. State lives in XFactorKpiSlice.
 */
export const useXFactorKpiGenerate = () => {
  const dispatch = useAppDispatch();
  const state = useAppSelector(s => s.xfactorKpi.generate);

  const generate = useCallback(
    async (
      accountId: string,
      measurementDate: string,
      datasetKind?: string | null,
      forceRefresh = false
    ): Promise<boolean> => {
      const result = await dispatch(
        generateKpiBriefing({
          accountId,
          measurementDate,
          datasetKind,
          forceRefresh,
        })
      );
      return generateKpiBriefing.fulfilled.match(result);
    },
    [dispatch]
  );

  return { ...state, generate };
};

export default useXFactorKpiGenerate;
